import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Table from "../../../../Components/UiComponents/Table/Table";
import { EditConvgIcon, Viewicon } from "../../../../Components/IconComponents";
import Confirmpopup from "../../Common/ConfirmPopup/Confirmpopup";
import { t } from "i18next";
import { toast } from "react-toastify";
import { postDataWithBody, getData } from "../../../../Services/Services";
import { getEventsdata, getEventsdataByIncident, getThemeURL, getUsersFromAppByRole } from "../../../../Services/EndPoints";

const EventTypeDetails = () => {
  const location = useLocation();
  const navigate = useNavigate();

  // Get incident data from navigation state or session storage
  // eslint-disable-next-line no-unused-vars
  const [incidentData, setIncidentData] = useState(() => {
    const dataFromLocation = location.state?.data;
    if (dataFromLocation) {
      sessionStorage.setItem('currentIncidentData', JSON.stringify(dataFromLocation));
      return dataFromLocation;
    }

    const savedData = sessionStorage.getItem('currentIncidentData');
    return savedData ? JSON.parse(savedData) : {};
  });

  const selectedIncident = incidentData?.incidentName || "";

  // States for dynamic data
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [filteredEventData, setFilteredEventData] = useState([]);
  const [userProfileLoaded, setUserProfileLoaded] = useState(false);
  //const [usersList, setUsersList] = useState([]);
  const [userIdToNameMap, setUserIdToNameMap] = useState({});
  const [userId, setUserId] = useState(null);
  const [allEventsClosed, setAllEventsClosed] = useState(false);
  const [userPermissions, setUserPermissions] = useState({
    canView: false,
    canEdit: false,
    canDelete: false,
  });
  const [user, setUser] = useState(null);
  const [canCreateEvent, setCanCreateEvent] = useState(false);


  useEffect(() => {
    const loadInitialData = async () => {
      await fetchUserProfile();
      await getUserProfile();
      setUserProfileLoaded(true);
    };

    loadInitialData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Role-based payload creation
  const createIncidentPayload = (userProfile) => {
    const { role_name, bank_id, branch_id, id, is_admin } = userProfile;

    // SUPER ADMIN role
    if (role_name === "SUPER ADMIN") {
      return {
        admin_role: "SA",
        user_id: null
      };
    }

    // HO role
    if (is_admin === true) {
      return {
        admin_role: "HO",
        userId: id || null
      };
    }

    // For other roles, determine based on bank_id and branch_id presence
    if (bank_id && branch_id) {
      // BRANCH_USER - has both bank_id and branch_id
      return {
        admin_role: "BRANCH_USER",
        userId: id || null
      };
    } else if (bank_id && !branch_id) {
      // BANK_USER - has only bank_id, no branch_id
      return {
        admin_role: "BANK_USER",
        userId: id || null
      };
    }

    // Default fallback
    return {
      admin_role: "U",
      bank_id: bank_id || null,
      branch_id: branch_id || null,
      userId: userId || null
    };
  };

  // Check if user is SUPER ADMIN or HO
  // const isSuperAdminOrHO = (user) => {
  //   return user && (user.role_name === "SUPER ADMIN" || user.is_admin === true);
  // };

  // For SUPER ADMIN and HO
const getEventsByIncidentIdForAdminHO = async () => {
  if (!incidentData?.id || !user) {
    console.log("Missing incident ID or user data");
    return;
  }

  setLoading(true);
  try {
    const payload = {
      ...createIncidentPayload(user),
      incident_id: incidentData.id
    };

    console.log("Fetching events with payload:", payload);

    const response = await postDataWithBody(getEventsdataByIncident, payload, {});
    
    let data = typeof response.json === 'function' ? await response.json() : response;
    setEvents(data || []);
    setFilteredEventData(data || []);

    // Check if all events are closed
    const allClosed = data && data.length > 0 && data.every(event => event.event_status === 752);
    setAllEventsClosed(allClosed);
  } catch (error) {
    console.error("Error fetching events data:", error);
    toast.error("Failed to load events data", {
      position: toast.POSITION.TOP_CENTER,
      autoClose: 3000,
    });
  } finally {
    setLoading(false);
  }
};
const getEventsByIncidentIdBank = async () => {
    if (!incidentData?.id || userId === null || userId === undefined) {
      console.log("Missing incident ID or user ID");
      return;
    }

    setLoading(true);
    try {
      const response = await getData(
        getEventsdata.replace("ID", incidentData.id).replace("UID", userId)
      );
      let data = typeof response.json === 'function' ? await response.json() : response;
      setEvents(data || []);
      setFilteredEventData(data || []);

      // Check if all events are closed
      const allClosed = data && data.length > 0 && data.every(event => event.event_status === 752);
      setAllEventsClosed(allClosed);
    } catch (error) {
      console.error("Error fetching events data:", error);
      toast.error("Failed to load events data", {
        position: toast.POSITION.TOP_CENTER,
        autoClose: 3000,
      });
    } finally {
      setLoading(false);
    }
  };

// Main function that calls the appropriate API based on user role
const getEventsByIncidentId = async () => {
  if (!user) {
    console.log("User data not available");
    return;
  }

  // Check if user is SUPER ADMIN or HO
  if (user.role_name === "SUPER ADMIN" || user.is_admin === true) {
    await getEventsByIncidentIdForAdminHO();
  } else {
    // For BANK_USER and BRANCH_USER
    await getEventsByIncidentIdBank();
  }
};

useEffect(() => {
  // Check if we have valid incident data
  if (!incidentData || Object.keys(incidentData).length === 0) {
    toast.error("No incident data available. Redirecting to incidents page...", {
      position: toast.POSITION.TOP_CENTER,
      autoClose: 3000,
    });

    setTimeout(() => navigate("../incidents"), 3000);
    return;
  }

  if (user && incidentData.id) {
    getEventsByIncidentId();
    
    // Check if current user can create events
    // SUPER ADMIN cannot create events
    // Other users can create events if they created the incident
    if (user.role_name === "SUPER ADMIN") {
      setCanCreateEvent(false);
    } else {
      // Check if current user created this incident
      const createdByCurrentUser = incidentData.created_by === user.id || 
                                    incidentData.createdBy === user.id ||
                                    incidentData.user_id === user.id;
      setCanCreateEvent(createdByCurrentUser);
    }
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [incidentData.id, user, userProfileLoaded]);

  // Filter data based on search
  useEffect(() => {
    if (searchQuery.trim() === "") {
      setFilteredEventData(events);
    } else {
      const query = searchQuery.toLowerCase();
      const filtered = events.filter(event =>
        (event.eventId && event.eventId.toLowerCase().includes(query)) ||
        (event.description && event.description.toLowerCase().includes(query)) ||
        (event.assignedToUser && (
          event.assignedToUser.toString().includes(query) ||
          (userIdToNameMap[event.assignedToUser] &&
            userIdToNameMap[event.assignedToUser].toLowerCase().includes(query))
        ))
      );
      setFilteredEventData(filtered);
    }
  }, [searchQuery, events, userIdToNameMap]);

  const parseUserPermissions = (webPermissions) => {
    if (!webPermissions) {
      return { canView: false, canEdit: false, canDelete: false };
    }
    const perms = webPermissions.split(',').map(x => x.trim().toUpperCase());
    return {
      canView: perms.includes('V'),
      canEdit: perms.includes('E'),
      canDelete: perms.includes('D'),
    };
  };

  // Status styling
  const renderStatusBadge = (status) => {
    let backgroundColor = "";
    let dotColor = "";

    switch (status) {
      case "Open":
        backgroundColor = "rgba(233, 80, 96, 0.5)";
        dotColor = "#E95060";
        break;
      case "Closed":
        backgroundColor = "rgba(0, 191, 40, 0.5)";
        dotColor = "#00BF28";
        break;
      case "In Progress":
        backgroundColor = "rgba(255, 161, 0, 0.5)";
        dotColor = "#FFA100";
        break;
      default:
        backgroundColor = "rgba(128, 128, 128, 0.5)";
        dotColor = "#808080";
    }

    return (
      <div className="status-badge-container" style={{ position: 'relative' }}>
        <div
          className="status-badge"
          style={{
            backgroundColor: backgroundColor,
            color: 'black',
            borderRadius: '5px',
            padding: '4px 16px 4px 20px',
            display: 'inline-block',
            width: '107px',
            height: '28px',
            lineHeight: '20px',
          }}
        >
          <div
            className="status-dot"
            style={{
              backgroundColor: dotColor,
              width: '8px',
              height: '8px',
              borderRadius: '50%',
              position: 'absolute',
              left: '8px',
              top: '10px'
            }}
          ></div>
          {status}
        </div>
      </div>
    );
  };

  // Criticality styling
  const renderCategory = (category) => {
    if (!category) return "NA";

    let textColor = "";

    switch (category.toUpperCase()) {
      case "CRITICAL":
        textColor = "#FF5D30";
        break;
      case "HIGH":
        textColor = "#F16A87";
        break;
      case "MEDIUM":
        textColor = "#FFA100";
        break;
      case "LOW":
        textColor = "#40BAC2";
        break;
      default:
        textColor = "#808080";
    }

    return <span style={{ color: textColor, fontWeight: 'bold' }}>{category}</span>;
  };

  // Format date helper function
  const formatDateTime = (dateString) => {
    if (!dateString) return "NA";

    try {
      const date = new Date(dateString);
      const formattedDate = date.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      });

      const formattedTime = date.toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      });

      return `${formattedDate} ${formattedTime}`;
    } catch (error) {
      console.error("Date formatting error:", error);
      return dateString;
    }
  };

  // Format date and time 
  const formatDateTimeIncident = (dateString) => {
    if (!dateString) return "N/A";

    const date = new Date(dateString);
    if (isNaN(date)) return "N/A";

    const dd = String(date.getDate()).padStart(2, "0");
    const mm = String(date.getMonth() + 1).padStart(2, "0");
    const yy = String(date.getFullYear()).slice(-2);

    let hours = date.getHours();
    const minutes = String(date.getMinutes()).padStart(2, "0");
    const ampm = hours >= 12 ? "PM" : "AM";
    hours = hours % 12 || 12;
    const formattedHours = String(hours).padStart(2, "0");

    const formattedTime = `${formattedHours}:${minutes} ${ampm}`;

    return `${dd}-${mm}-${yy} ${formattedTime}`;
  };

  // Helper function to store event data and navigate to event details
  const storeEventAndNavigate = (path, eventData) => {
    sessionStorage.setItem('currentEventData', JSON.stringify(eventData));
    navigate(path, {
      state: {
        eventData: eventData,
        incidentData: incidentData,
      },
    });
  };

  // Get username from user ID using the mapping we created
  const getUsernameById = (userId) => {
    if (!userId) return "NA";
    return userIdToNameMap[userId] || userId;
  };

  // Columns for the events data table 
  const eventColumns = React.useMemo(
    () => [
      {
        Header: t("Event ID"),
        accessor: "eventId",
        disableSortBy: false,
        Cell: ({ value, row }) => (
          <span
            className="clickable-event-id"
            style={{
              cursor: 'pointer',
              textDecoration: 'underline'
            }}
            onClick={() => storeEventAndNavigate("../event-details", row.original)}
          >
            {value || "NA"}
          </span>
        ),
      },
      {
        Header: t("Event Name"),
        accessor: "eventName",
        disableSortBy: true,
        Cell: ({ value, row }) => (
          <span
            className="clickable-event-id"
            style={{
              cursor: 'pointer',
              textDecoration: 'underline'
            }}
            onClick={() => storeEventAndNavigate("../event-details", row.original)}
          >
            {value || "NA"}
          </span>
        ),
      },
      {
        Header: t("Source System"),
        accessor: "sourceSystem",
        disableSortBy: true,
        Cell: ({ value }) => value || "NA",
      },
      {
        Header: t("Severity"),
        accessor: "severityLevel",
        disableSortBy: true,
        Cell: ({ value }) => renderCategory(value)
      },
      {
        Header: t("SLA Target"),
        accessor: "slaTarget",
        disableSortBy: true,
        Cell: ({ value }) => value || "NA",
      },
      {
        Header: t("Status"),
        accessor: "event_status",
        disableSortBy: false,
        Cell: ({ value }) => {
          let status = "Open";

          if (value === 749) {
            status = "Open";
          } else if (value === 750) {
            status = "In Progress";
          } else if (value === 752) {
            status = "Closed";
          }

          return renderStatusBadge(status);
        }
      },
      {
        Header: t("Assigned To"),
        accessor: "assignedToUserName",
        disableSortBy: true,
        Cell: ({ value }) => getUsernameById(value),
      },
      ...(userPermissions.canView || userPermissions.canEdit || userPermissions.canDelete
      ? [{
        Header: t("Actions"),
        disableSortBy: true,
        Cell: (props) => {
          const [isdelete, setIsdelete] = useState(false);
          const status = props.row.original.event_status;
          const isClosed = status === 752;

          const deletePopUp = () => setIsdelete(!isdelete);

          const deleteEvent = async (id) => {
            try {
              const response = await fetch(`/api/events/${id}`, { method: 'DELETE' });
              if (response.ok) {
                setIsdelete(false);
                await getEventsByIncidentId();
                toast.success(t("delete_success_message"), {
                  position: toast.POSITION.TOP_CENTER,
                  autoClose: 2000,
                });
              } else {
                toast.error(t("delete_failure_message"), {
                  position: toast.POSITION.TOP_CENTER,
                  autoClose: 2000,
                });
              }
            } catch (error) {
              console.error("Error deleting event:", error);
              toast.error(t("delete_failure_message"), {
                position: toast.POSITION.TOP_CENTER,
                autoClose: 2000,
              });
            }
          };

             // Check if edit should be disabled for HO role
          const isHORole = user?.is_admin === true;
          const createdByCurrentUser = props.row.original.created_by === userId || 
                                        props.row.original.createdBy === userId ||
                                        props.row.original.user_id === userId;
          const isEditDisabled = isClosed || (isHORole && !createdByCurrentUser);


          return (
            <div className="usertable__actionsicon" style={{ display: 'flex', gap: '10px' }}>
              {/* View action */}
              {userPermissions.canView && (
                <span onClick={() => storeEventAndNavigate("../event-details", props.row.original)}>
                  <Viewicon className="cursor-pointer me-2" />
                </span>
              )}
              {/* Edit action */}
               {userPermissions.canEdit && (
                <span
                  onClick={() => {
                    if (!isEditDisabled) {
                      navigate("../create-event", {
                        state: { data: props.row.original, edit: true },
                      });
                    }
                  }}
                  style={{ 
                    cursor: isEditDisabled ? "not-allowed" : "pointer", 
                    opacity: isEditDisabled ? 0.5 : 1 
                  }}
                  title={isEditDisabled && isHORole && !createdByCurrentUser ? "You can only edit events you created" : ""}
                >
                  <EditConvgIcon />
                </span>
              )}
              {isdelete && (
                <Confirmpopup
                  className="DeltePopup"
                  show={isdelete}
                  title={t("deletepopuptext")}
                  message={t("deletepopupmessage")}
                  handleToggle={deletePopUp}
                  row={props.row}
                  name={props.row.original.eventId || props.row.original.id}
                  deleteRow={deleteEvent}
                  cancel={t("cancelbutton")}
                  action={t("actionbutton")}
                />
              )}
            </div>
          );
        },
      }]
      : []),
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t, navigate, incidentData, userIdToNameMap, userPermissions]
  );

  // Function to handle search input changes
  const handleSearch = (e) => {
    setSearchQuery(e.target.value);
  };

  // Navigate back to incidents page
  // const goBackToIncidents = () => {
  //   navigate("../incidents");
  // };

  const fetchUserProfile = async () => {
    try {
      const response = await getData(getThemeURL);
      if (response && Array.isArray(response) && response.length > 0) {
        const userProfile = response[0];
        setUserId(userProfile.id);
        setUser(userProfile);
        const permissions = parseUserPermissions(userProfile.web_permissions);
        setUserPermissions(permissions);
      }
    } catch (error) {
      console.error("Error fetching user profile:", error);
    }
  };

  const getUserProfile = async () => {
    try {
      const userProfile = await getData(getThemeURL);

      if (userProfile && userProfile.length > 0) {
        getUserList(userProfile[0]);
      }
    } catch (error) {
      console.error("Error fetching user profile:", error);
      toast.error("Failed to fetch user profile", {
        position: toast.POSITION.TOP_CENTER,
        autoClose: 2000,
      });
    }
  };

  // Role-based payload creation for user list
  const createPayload = (profile) => {
   if (profile.role_name === "SUPER ADMIN") {
      return {
        admin_role: "SA",
        bank_id: profile.bank_id || null,
        branch_id: profile.branch_id || null,
      };
    } else if (profile.is_admin === true) {
      return {
        admin_role: "HO",
        bank_id: profile.bank_id || null,
        userId: profile.id,
      };
    }
    else {
      return {
        admin_role: "U",
        bank_id: profile.bank_id || null,
        branch_id: profile.branch_id || null,
      };
    }
  };

  // Updated getUserList function to store user data and create mapping
  const getUserList = async (profile) => {
    try {
      const payload = createPayload(profile);
      const userList = await postDataWithBody(getUsersFromAppByRole, payload, {});

      //setUsersList(userList || []);

      const userMapping = {};
      if (Array.isArray(userList)) {
        userList.forEach(user => {
          const userId = user.id;
          const userName = user.user_name ||
            (user.first_name && user.last_name ?
              `${user.first_name} ${user.last_name}` :
              `User ${userId}`);

          if (userId) {
            userMapping[userId] = userName;
          }
        });
      }

      setUserIdToNameMap(userMapping);

    } catch (error) {
      console.error("Error fetching user list:", error);
      toast.error("Failed to load users data", {
        position: toast.POSITION.TOP_CENTER,
        autoClose: 3000,
      });
    }
  };

  // Add new event handler
  const addNewHandler = () => {
    const formattedIncidentData = {
      ...incidentData,
      eventOccurred: formatDateTime(incidentData.incidentDateAndTime),
    };

    const navigationState = {
      incidentData: formattedIncidentData,
      eventType: selectedIncident,
    };

    navigate("../create-event", {
      state: navigationState,
    });
  };

  return (
    <div className="event-type-details-container" style={{ padding: "20px" }}>
      {/* Incident Details Section */}
      <div className="bg-white rounded mb-4 p-4">
        <h5 style={{ color: "#1a7a73", marginBottom: '20px' }}>
          Incident Details
        </h5>

        <div className="incident-details-simple">
          <div className="row mb-3">
            <div className="col-3">
              <div className="detail-label" style={{ fontWeight: "600", color: "#FFA100" }}>Incident ID</div>
              <div className="detail-value" >{incidentData.incidentId || "NA"}</div>
            </div>

            <div className="col-3">
              <div className="detail-label" style={{ fontWeight: "600", color: "#FFA100" }}>Incident Name</div>
              <div className="detail-value" >{incidentData.incidentName || "NA"}</div>
            </div>

            <div className="col-3">
              <div className="detail-label" style={{ fontWeight: "600", color: "#FFA100" }}>Severity</div>
              <div className="detail-value">{incidentData.severityLevel}</div>
            </div>

            <div className="col-3">
              <div className="detail-label" style={{ fontWeight: "600", color: "#FFA100", width: "150px" }}>Incident Date & Time</div>
              <div className="detail-value">{formatDateTimeIncident(incidentData.incidentDateAndTime)}</div>
            </div>
          </div>
          <div className="row mb-3">
            <div className="col-12">
              <div className="incident-description-label" style={{ fontWeight: "600", color: "#FFA100" }}>Incident Description</div>
              <div className="detail-value" >{incidentData.description || "NA"}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Events Data Table */}
      <div className="mt-4">
        <div className="bg-white rounded">
          <div className="d-flex justify-content-between align-items-center p-3">
            <h5 style={{ color: "#1a7a73", marginTop: '-50px', marginLeft: '10px' }}>
              {'Events Data'}
            </h5>
          </div>
          <div className="table-section" style={{
            overflow: "auto",
            padding: "0 10px 10px 10px",
            marginTop: '-40px'
          }}>
            <Table
              data={filteredEventData}
              addButtonLabel={'Create Event'}
              addNewHandler={addNewHandler}
              allEventsClosed={allEventsClosed}
              is_disable={incidentData.is_disable || !canCreateEvent}
              title=""
              showFilter={false}
              columns={eventColumns}
              numberOfRows={5}
              showExport={false}
              showReset={false}
              onExpand={false}
              showSearch={selectedIncident ? true : false}
              searchPlaceholder="Search events..."
              onSearchChange={handleSearch}
              searchValue={searchQuery}
              isHeaderFixed={true}
              rowCount={filteredEventData.length}
              headerTableHeadStyle={{ background: "#1a7a73", color: "#FFFFFF" }}
              tableStyle={{
                border: "1px solid #f0f0f0",
                borderRadius: "0 0 12px 12px",
                margin: 0,
                width: "100%",
                padding: "0px",
                marginLeft: "15px"
              }}
              loading={loading}
              emptyTableContent={
                <div style={{
                  padding: "30px 0",
                  textAlign: "center",
                  color: "#666"
                }}>
                  {loading ?
                    "Loading events data..." :
                    selectedIncident ?
                      "No event data available for this incident." :
                      "No events data available."
                  }
                </div>
              }
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default EventTypeDetails;